import { set } from '@ember/object';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type CompanyModel from 'garaje/models/company';
import type ConfigModel from 'garaje/models/config';
import type ConnectLocationConfigurationModel from 'garaje/models/connect-location-configuration';
import type DeviceModel from 'garaje/models/device';
import type IdScanConfigurationModel from 'garaje/models/id-scan-configuration';
import type LocationModel from 'garaje/models/location';
import type LocationSubscriptionModel from 'garaje/models/location-subscription';
import type PluginModel from 'garaje/models/plugin';
import type PluginInstallModel from 'garaje/models/plugin-install';
import type SubscriptionModel from 'garaje/models/subscription';
import type VisualComplianceConfigurationModel from 'garaje/models/visual-compliance-configuration';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import type TransitionConfirmService from 'garaje/services/transition-confirm';
import type VisitorsOnboardingService from 'garaje/services/visitors-onboarding';
import { catchError } from 'garaje/utils/catch-error';
import { routeEvent } from 'garaje/utils/decorators/route';
import type { RecordArray } from 'garaje/utils/type-utils';
import _intersection from 'lodash/intersection';
import { hash } from 'rsvp';

import type SecurityIndexController from './controller';

const companyHasSecurityFeatures = ({
  vrSubscription,
  plugins,
  company,
  state,
}: {
  vrSubscription: LocationSubscriptionModel | SubscriptionModel | null;
  plugins: RecordArray<PluginModel>;
  company: CompanyModel | null;
  state: StateService;
}) => {
  const securityPluginIds = plugins
    .filter((plugin) => ['security', 'wi-fi'].includes(plugin.category))
    .map((plugin) => plugin.id);
  return (
    vrSubscription?.isEnterprisePlan ||
    vrSubscription?.canAccessBlocklist ||
    state.features?.canAccessVisualCompliance ||
    state.features?.canAccessIdScanning ||
    isPresent(_intersection(company?.adhocPlugins, securityPluginIds))
  );
};

export interface SecurityIndexRouteModel {
  company: CompanyModel | null;
  vrSubscription: LocationSubscriptionModel | SubscriptionModel | null;
  isConnectedToProperty: boolean;
  connectLocationConfiguration: ConnectLocationConfigurationModel | undefined;
  currentLocation: LocationModel;
  ipads?: RecordArray<DeviceModel>;
  plugins: RecordArray<PluginModel>;
  pluginInstalls: RecordArray<PluginInstallModel>;
  visualComplianceConfiguration?: VisualComplianceConfigurationModel;
  idScanConfiguration?: IdScanConfigurationModel;
  config: ConfigModel;
}

export default class SecurityIndexRoute extends Route {
  declare controller: SecurityIndexController;

  @service declare abilities: AbilitiesService;
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: Store;
  @service declare transitionConfirm: TransitionConfirmService;
  @service declare visitorsOnboarding: VisitorsOnboardingService;

  titleToken = 'Security';

  beforeModel(): void {
    if (this.abilities.cannot('visit security')) {
      void this.router.transitionTo('unauthorized');
    }
  }

  async model(): Promise<SecurityIndexRouteModel> {
    const currentLocation = this.state.currentLocation;
    // users might have access to other settings but not devices/ipads
    const ipads = catchError(this.store.query('device', { filter: { location: currentLocation.id } }));
    const vrSubscription = this.state.vrSubscription;
    const company = this.state.currentCompany;
    // users might have access to other settings but not visual compliance
    const visualComplianceConfiguration = catchError(currentLocation.visualComplianceConfiguration);
    const plugins = this.store.query('plugin', {
      filter: { location: currentLocation.id },
    });
    const pluginInstalls = this.store.query('plugin-install', {
      filter: { location: currentLocation.id },
    });

    const isConnectedToProperty = await currentLocation.isConnectedToProperty();
    let connectLocationConfiguration: ConnectLocationConfigurationModel | undefined;

    if (this.featureFlags.isEnabled('connect-pre-reg-acs-qr-code') && isConnectedToProperty) {
      connectLocationConfiguration = await currentLocation.getConnectConfiguration();
    }

    await this.visitorsOnboarding.loadSteps();

    return hash({
      company,
      isConnectedToProperty,
      // reload location in case blocklist contacts have changed
      currentLocation: currentLocation.reload(),
      connectLocationConfiguration,
      ipads,
      plugins,
      pluginInstalls,
      visualComplianceConfiguration,
      idScanConfiguration:
        this.featureFlags.isEnabled('id-name-mismatch-screener') ||
        this.featureFlags.isEnabled('id-face-match-screener')
          ? currentLocation.loadIdScanConfiguration()
          : undefined,
      vrSubscription,
      config: currentLocation.config,
    });
  }

  redirect(_model: SecurityIndexRouteModel, transition: Transition): void {
    this.visitorsOnboarding.gateRoute(this, transition);
  }

  setupController(controller: SecurityIndexController, model: SecurityIndexRouteModel, transition: Transition): void {
    super.setupController(controller, model, transition);
    const { vrSubscription, plugins, company } = model;

    const isUpgradeModalInitiallyVisible =
      !companyHasSecurityFeatures({ vrSubscription, plugins, company, state: this.state }) &&
      !controller.createSalesforceLead.lastSuccessful;
    set(controller, 'isUpgradeModalVisible', isUpgradeModalInitiallyVisible);
    // Under MPB the route can re-enter on a location switch — collapse the
    // panel so the prior location's edit state doesn't bleed into a tier
    // that shouldn't render the body. locationBilling doesn't support mixed
    // tiers, so the bleed-through can't happen there.
    if (this.featureFlags.isEnabled('company-has-mixed-plan-billing')) {
      set(controller, 'isEditingBlockList', false);
    }
  }

  @routeEvent
  routeWillChange(transition: Transition): void {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = this.controller;
    const { idScanningHasChanges } = controller;
    // no need to display confirmation modal when nothing is changed
    if (!idScanningHasChanges) {
      return;
    }
    // display confirmation modal if settings are dirty
    void this.transitionConfirm.displayConfirmTask.perform(transition, {
      continue() {
        if (idScanningHasChanges) {
          controller.send('rollbackIdScanning');
        }
      },
    });
  }
}
